import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import type { HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { API_BASE_URL } from './api-base-url.token';
import type { ApiResponse } from './api-envelope';
import { makeIdempotencyKey } from './idempotency-key';

export interface RequestOptions {
  params?: Record<string, string | number | boolean | null | undefined>;
  headers?: Record<string, string>;
  /** When true (default for POST), an `Idempotency-Key` header is
   *  generated automatically. Set false when the endpoint isn't
   *  idempotent (rare — most state-changing POSTs are). */
  idempotent?: boolean;
  context?: HttpContext;
}

/**
 * Thin wrapper over Angular's `HttpClient`. Three jobs:
 *   1. Prefix every URL with the configured `API_BASE_URL`.
 *   2. Unwrap the `{ data, meta }` envelope so call sites see plain T.
 *   3. Auto-stamp `Idempotency-Key` on POST so backend
 *      `@Idempotent()` paths don't 400.
 *
 * Auth token injection happens in `shared-auth`'s HTTP interceptor,
 * not here — keeps the HTTP layering single-responsibility.
 */
@Injectable({ providedIn: 'root' })
export class ApiClientService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  get<T>(path: string, opts: RequestOptions = {}): Observable<T> {
    return this.http
      .get<ApiResponse<T>>(this.url(path), this.opts(opts, false))
      .pipe(map((r) => r.data));
  }

  /**
   * Returns both `data` and `meta` — useful for paginated lists where
   * the caller needs `meta.pagination.total`.
   */
  getWithMeta<T>(path: string, opts: RequestOptions = {}): Observable<ApiResponse<T>> {
    return this.http.get<ApiResponse<T>>(this.url(path), this.opts(opts, false));
  }

  post<T, B = unknown>(path: string, body: B, opts: RequestOptions = {}): Observable<T> {
    return this.http
      .post<ApiResponse<T>>(this.url(path), body, this.opts(opts, true))
      .pipe(map((r) => r.data));
  }

  /**
   * POST returning both `data` and `meta` — useful when the endpoint
   * reports a best-effort side-effect (e.g. `meta.portalInvite`) that the
   * caller needs to surface separately from the primary payload.
   */
  postWithMeta<T, B = unknown>(
    path: string,
    body: B,
    opts: RequestOptions = {},
  ): Observable<ApiResponse<T>> {
    return this.http.post<ApiResponse<T>>(this.url(path), body, this.opts(opts, true));
  }

  patch<T, B = unknown>(path: string, body: B, opts: RequestOptions = {}): Observable<T> {
    return this.http
      .patch<ApiResponse<T>>(this.url(path), body, this.opts(opts, false))
      .pipe(map((r) => r.data));
  }

  put<T, B = unknown>(path: string, body: B, opts: RequestOptions = {}): Observable<T> {
    return this.http
      .put<ApiResponse<T>>(this.url(path), body, this.opts(opts, false))
      .pipe(map((r) => r.data));
  }

  delete<T = void>(path: string, opts: RequestOptions = {}): Observable<T> {
    return this.http
      .delete<ApiResponse<T> | null>(this.url(path), this.opts(opts, false))
      // A `204 No Content` resolves with a null body — guard the unwrap
      // so callers of endpoints that don't echo an envelope don't throw.
      .pipe(map((r) => (r?.data ?? undefined) as T));
  }

  /**
   * POST a `FormData` body (multipart/form-data). The `content-type`
   * header is intentionally omitted so the browser auto-sets the
   * `multipart/form-data; boundary=…` value. Auth token is still
   * attached by the `shared-auth` interceptor. Unwraps the standard
   * `{ data, meta }` envelope and resolves with `T`.
   */
  postFormData<T>(path: string, body: FormData, opts: RequestOptions = {}): Observable<T> {
    // Build params the same way as opts() but skip the JSON content-type.
    let params = new HttpParams();
    if (opts.params) {
      for (const [k, v] of Object.entries(opts.params)) {
        if (v === null || v === undefined) continue;
        params = params.set(k, String(v));
      }
    }
    const extraHeaders: Record<string, string> = {
      ...(opts.headers ?? {}),
      ...((opts.idempotent ?? true) ? { 'Idempotency-Key': makeIdempotencyKey() } : {}),
    };
    return this.http
      .post<ApiResponse<T>>(this.url(path), body, {
        params,
        headers: new HttpHeaders(extraHeaders),
        context: opts.context,
      })
      .pipe(map((r) => r.data));
  }

  /**
   * GET a non-JSON payload (e.g. a CSV export) as a `Blob`. Skips the
   * envelope unwrap — the response body is the file itself. The auth
   * token is still attached by the `shared-auth` interceptor.
   */
  getBlob(path: string, opts: RequestOptions = {}): Observable<Blob> {
    let params = new HttpParams();
    if (opts.params) {
      for (const [k, v] of Object.entries(opts.params)) {
        if (v === null || v === undefined) continue;
        params = params.set(k, String(v));
      }
    }
    return this.http.get(this.url(path), {
      params,
      headers: new HttpHeaders(opts.headers ?? {}),
      responseType: 'blob',
    });
  }

  // ---- internals ----

  private url(path: string): string {
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    const trimmedBase = this.baseUrl.replace(/\/$/, '');
    const trimmedPath = path.startsWith('/') ? path : `/${path}`;
    return `${trimmedBase}${trimmedPath}`;
  }

  private opts(opts: RequestOptions, defaultIdempotent: boolean) {
    const headers = new HttpHeaders({
      'content-type': 'application/json',
      ...(opts.headers ?? {}),
      ...((opts.idempotent ?? defaultIdempotent)
        ? { 'Idempotency-Key': makeIdempotencyKey() }
        : {}),
    });
    let params = new HttpParams();
    if (opts.params) {
      for (const [k, v] of Object.entries(opts.params)) {
        if (v === null || v === undefined) continue;
        params = params.set(k, String(v));
      }
    }
    return { headers, params, context: opts.context };
  }
}
